import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';

import { BehaviorSubject, combineLatest, filter, map, NEVER, Observable, Subject, Subscription } from 'rxjs';
import { Store } from '@ngrx/store';

import {
  ETICKET_PATTERN,
  NAME_PATTERN,
  passengersNotCheckedInExist,
  PNR_PATTERN_WITH_TRAILING_WHITESPACE,
} from '@fcom/core/utils';
import { RedirectService } from '@fcom/common-booking/modules/redirect-service';
import { OrderActions, PassengerNameRecord } from '@fcom/common-booking';
import { CmsArticle, CmsCollection, CmsImageData } from '@fcom/core-api/interfaces';
import { FragmentService } from '@fcom/common/services';
import { getError, StateActions } from '@fcom/manage-booking/store';
import {
  FinnairBoundItem,
  FinnairCheckInVersion,
  FinnairOrder,
  RequestInitiator,
  TourOperatorCode,
} from '@fcom/dapi/api/models';
import { ButtonType, LoaderTheme, ModalButtons, NotificationLayout, NotificationTheme } from '@fcom/ui-components';
import { CommonBookingAncillaryService } from '@fcom/common-booking/modules/ancillaries/services';
import { NativeBridgeService, RootPaths } from '@fcom/core';
import { LanguageService } from '@fcom/ui-translate';

import { ManageBookingService } from '../../../../services';
import { ManageBookingFeatureState } from '../../../../interfaces';
import { MmbError, MmbStage } from '../../../../enums';
import { PnrFormService } from '../../../../services/pnr-form-service';

@Component({
  selector: 'fin-check-in-form',
  templateUrl: 'check-in-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CheckInFormComponent implements OnInit, OnDestroy {
  readonly MmbError = MmbError;
  readonly ModalButtons = ModalButtons;
  readonly NotificationTheme = NotificationTheme;
  readonly NotificationLayout = NotificationLayout;
  readonly ButtonType = ButtonType;
  readonly LoaderTheme = LoaderTheme;

  @Input() backgroundImage: CmsImageData;
  @Input() disruptions$: Observable<CmsCollection> = NEVER;
  @Input() loading: boolean;

  recLocRegex = new RegExp(PNR_PATTERN_WITH_TRAILING_WHITESPACE.source + '|' + ETICKET_PATTERN.source);

  mainError$: Observable<string>;
  groupModalError$: Observable<MmbError>;
  showGroupBookingModal$ = new BehaviorSubject(false);

  GROUP_ERRORS = [MmbError.GROUP_BOOKING, MmbError.GROUP_BOOKING_NOT_FOUND];
  GROUP_MODAL_ERRORS = [MmbError.GROUP_BOOKING_NOT_FOUND, MmbError.GROUP_BOOKING_LOGGING_WITH_INFANT];

  cancelRetrieve$: Subject<boolean> = new Subject<boolean>();

  cmsArticle$: Observable<CmsArticle>;

  groupFirstNameForm: FormGroup<{
    firstName: FormControl<string>;
    lastName: FormControl<string>;
    recLoc: FormControl<string>;
    tourOperatorCode: FormControl<TourOperatorCode>;
  }>;

  NAME_VALIDATORS = [
    Validators.required,
    Validators.minLength(2),
    Validators.maxLength(50),
    Validators.pattern(NAME_PATTERN),
  ];

  order$: Observable<FinnairOrder>;
  bound$: Observable<FinnairBoundItem>;
  canActivateCheckInTrigger$: Observable<boolean>;
  initialValues$: Observable<PassengerNameRecord>;

  private subscriptions: Subscription = new Subscription();

  constructor(
    private store$: Store<ManageBookingFeatureState>,
    private mmbService: ManageBookingService,
    private fragmentService: FragmentService,
    private redirectService: RedirectService,
    private commonBookingAncillaryService: CommonBookingAncillaryService,
    private pnrFormService: PnrFormService,
    private router: Router,
    private languageService: LanguageService,
    private nativeBridgeService: NativeBridgeService
  ) {}

  ngOnInit(): void {
    this.cmsArticle$ = this.fragmentService.getFragment('MMB.fragments.checkInInfo.url');

    const error$ = this.store$.pipe(getError());
    this.subscriptions.add(
      error$.subscribe((error) => {
        if (this.GROUP_ERRORS.includes(error)) {
          this.showGroupBookingModal$.next(true);
        }
      })
    );

    this.groupModalError$ = error$.pipe(filter((error) => error && this.GROUP_MODAL_ERRORS.includes(error)));
    this.mainError$ = combineLatest([this.showGroupBookingModal$, error$]).pipe(
      filter(([show]) => !show),
      map(([_, error]) => (error ? 'MMB.login.errors.' + error : null))
    );

    const { initialValues$, deepLinkRouteChange$ } = this.pnrFormService.getInitialValues();

    this.initialValues$ = initialValues$;

    this.subscriptions.add(
      deepLinkRouteChange$.subscribe((record: PassengerNameRecord) => {
        this.onSubmit(record, true, RequestInitiator.DEEPLINK_CHECK_IN);
      })
    );
  }

  submitGroupFirstName(): void {
    if (this.groupFirstNameForm.valid) {
      this.onSubmit(this.groupFirstNameForm.value as PassengerNameRecord, true);
    }
  }

  onSubmit(event: PassengerNameRecord | null, resetStage = false, requestInitiator = RequestInitiator.CHECK_IN): void {
    this.groupFirstNameForm = new FormGroup({
      firstName: new FormControl(event.firstName ?? '', this.NAME_VALIDATORS),
      lastName: new FormControl(event.lastName),
      recLoc: new FormControl(event.recLoc),
      tourOperatorCode: new FormControl(event.tourOperatorCode),
    });

    this.order$ = this.mmbService.retrieveOrderFromForm(
      event,
      requestInitiator,
      true,
      resetStage,
      this.cancelRetrieve$,
      true
    );
    this.canActivateCheckInTrigger$ = this.order$.pipe(
      map((order) => {
        return (
          order.eligibilities?.checkIn?.some(
            (e) =>
              e.isAllowedToUseCheckIn &&
              e.version === FinnairCheckInVersion.NEXTGEN &&
              passengersNotCheckedInExist(e.flightTravelerEligibility)
          ) ?? false
        );
      })
    );
    this.bound$ = this.order$.pipe(
      map((order: FinnairOrder) => {
        const boundId = order.eligibilities?.checkIn?.find(
          ({ version, isAllowedToUseCheckIn }) => version === FinnairCheckInVersion.NEXTGEN && isAllowedToUseCheckIn
        )?.id;

        return order.bounds.find(({ id }) => id === boundId);
      })
    );

    this.subscriptions.add(
      this.order$.pipe(filter(() => this.nativeBridgeService.isInsideNativeWebView)).subscribe({
        next: (order) => {
          const isEligibleForNextGen = order.eligibilities?.checkIn?.some(
            (e) => e.isAllowedToUseCheckIn && e.version === FinnairCheckInVersion.NEXTGEN
          );

          this.nativeBridgeService.onCheckInLoaded({ status: isEligibleForNextGen });
        },
        error: () => {
          this.nativeBridgeService.onCheckInLoaded({ status: false });
        },
      })
    );

    this.subscriptions.add(
      this.order$.subscribe((order) => {
        this.store$.dispatch(OrderActions.setOrderData({ order }));
        this.store$.dispatch(StateActions.setLoading({ loading: false }));
        this.commonBookingAncillaryService.setServiceCatalog(order);

        const firstEligibleBoundEligibility = order.eligibilities?.checkIn?.find((e) => e.isAllowedToUseCheckIn);
        const isEligibleForTravelReady =
          firstEligibleBoundEligibility &&
          (order.eligibilities?.travelReady?.find((e) => e.id === firstEligibleBoundEligibility.id)?.hub
            ?.isAllowedToUse ??
            false);
        const redirectForm = firstEligibleBoundEligibility?.redirect;
        const isNewCheckInFlow = firstEligibleBoundEligibility?.version === FinnairCheckInVersion.NEXTGEN;

        if (isEligibleForTravelReady) {
          this.router.navigateByUrl(`${this.languageService.langValue}/${RootPaths.TRAVEL_READY}`);
        } else if (!isNewCheckInFlow && redirectForm) {
          this.redirectService.redirectToCheckIn(order);
        } else if (
          !isNewCheckInFlow ||
          (isNewCheckInFlow && !passengersNotCheckedInExist(firstEligibleBoundEligibility?.flightTravelerEligibility))
        ) {
          this.store$.dispatch(StateActions.setStage({ stage: MmbStage.VIEW, fromCheckIn: true }));
        }
      })
    );
  }

  cancelRetrieve(): void {
    this.store$.dispatch(StateActions.setLoading({ loading: false }));
    this.cancelRetrieve$.next(true);
  }

  onFirstNameModalClose(): void {
    this.groupFirstNameForm.controls.firstName.setValue('');
    this.store$.dispatch(StateActions.clearError());
    this.showGroupBookingModal$.next(false);
  }

  closeErrorNotification(): void {
    this.store$.dispatch(StateActions.clearError());
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
